import { isOffsetDateTime, isUuid } from '@/lib/validation';
import type { ServiceClient } from '../http/client';
import { ApiError } from '../http/errors';
import type { EzremsTransitionCommand } from './ezrems-transition-command';

/**
 * Unit별 EZREMS 판매 전환 — 로컬 BE candidate(010, 미배포)의 OpenAPI·답변(fe-answers) 기준이다.
 * POST 202는 완료가 아니라 접수 영수증이다. 결과는 같은 unit_id·원 command UUID의 status GET으로만 확인하고,
 * UNKNOWN을 새 UUID나 자동 POST로 우회하지 않는다.
 */
export interface EzremsUnitTransitionPreview {
  room_id: string;
  unit_id: string;
  unit_version: number;
  unit_ids: string[];
  sale_revision: number;
  sale_product: string | null;
  pending: boolean;
  contract_status:
    | 'UNKNOWN'
    | 'UNBOUND'
    | 'OBSERVED_NO_FUTURE'
    | 'FUTURE_CONTRACT'
    | 'DRAFT_CONTRACT';
  eligible: boolean;
  current_end: string | null;
  earliest_date: string | null;
  provider_kind: string | null;
  write_supported: boolean;
  /** OpenAPI는 문자열|null만 선언한다 — Room의 닫힌 값 집합을 Unit에 추정해 적용하지 않는다. */
  write_unavailable_reason: string | null;
}
export interface EzremsUnitTransitionReceipt {
  command_id: string;
  room_id: string;
  unit_id: string;
  revision: number;
  product: string;
  channel: string;
  first_move_in_date: string | null;
  /** 최종 성공 증거가 아니다. */
  provider_outcome: string | null;
}
export interface EzremsUnitTransitionStatus {
  command_id: string;
  room_id: string;
  unit_id: string;
  target: 'MIDTERM' | 'GENERAL';
  outcome: 'PENDING' | 'UNKNOWN' | 'APPLIED' | 'NOT_APPLIED';
  transport: 'UNKNOWN' | 'DELIVERED';
  readback_matches: boolean | null;
  reason: string | null;
  current_sale_revision: number;
  pending: boolean;
}

const record = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);
/** sale_scope는 OpenAPI required에 없어 있으면 'UNIT'이어야 한다. */
const exact = (v: Record<string, unknown>, keys: readonly string[]) => {
  const present = Object.keys(v).filter((key) => key !== 'sale_scope');
  return (
    present.length === keys.length &&
    keys.every((key) => Object.hasOwn(v, key)) &&
    (!Object.hasOwn(v, 'sale_scope') || v.sale_scope === 'UNIT')
  );
};
const revision = (v: unknown): v is number =>
  typeof v === 'number' && Number.isSafeInteger(v) && v >= 0;
const positive = (v: unknown): v is number => revision(v) && v > 0;
const nullableString = (v: unknown): v is string | null => v === null || typeof v === 'string';
const date = (v: unknown) =>
  v === null ||
  (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v) && isOffsetDateTime(`${v}T00:00:00Z`));
const sameId = (v: unknown, expected: string) =>
  isUuid(v) && v.toLowerCase() === expected.toLowerCase();
const invalid = (message: string, status?: number) =>
  new ApiError({ kind: 'invalid-response', service: 'core', status, message });
const contractStatuses = [
  'UNKNOWN',
  'UNBOUND',
  'OBSERVED_NO_FUTURE',
  'FUTURE_CONTRACT',
  'DRAFT_CONTRACT',
];

const previewKeys = [
  'room_id',
  'unit_ids',
  'sale_revision',
  'sale_product',
  'pending',
  'contract_status',
  'eligible',
  'current_end',
  'earliest_date',
  'provider_kind',
  'write_supported',
  'write_unavailable_reason',
  'unit_id',
  'unit_version',
] as const;

export async function readEzremsUnitTransitionPreview(
  client: ServiceClient,
  unitId: string,
  signal?: AbortSignal,
): Promise<EzremsUnitTransitionPreview> {
  const bad = () => invalid('현재 슬롯의 계약과 전환 준비 정보를 확인하지 못했습니다.');
  if (!isUuid(unitId)) throw bad();
  const v = await client.get<unknown>(`admin/units/${unitId}/ezrems-transition-preview`, {
    headers: { 'Cache-Control': 'no-store' },
    signal,
  });
  if (
    !record(v) ||
    !exact(v, previewKeys) ||
    !sameId(v.unit_id, unitId) ||
    !positive(v.unit_version) ||
    !isUuid(v.room_id) ||
    !Array.isArray(v.unit_ids) ||
    !v.unit_ids.every(isUuid) ||
    !v.unit_ids.some((id: string) => id.toLowerCase() === unitId.toLowerCase()) ||
    new Set(v.unit_ids.map((id: string) => id.toLowerCase())).size !== v.unit_ids.length ||
    !revision(v.sale_revision) ||
    !nullableString(v.sale_product) ||
    typeof v.pending !== 'boolean' ||
    typeof v.eligible !== 'boolean' ||
    typeof v.write_supported !== 'boolean' ||
    !contractStatuses.includes(String(v.contract_status)) ||
    !date(v.current_end) ||
    !date(v.earliest_date) ||
    !nullableString(v.provider_kind) ||
    !nullableString(v.write_unavailable_reason)
  )
    throw bad();
  return v as unknown as EzremsUnitTransitionPreview;
}

const receiptKeys = [
  'command_id',
  'room_id',
  'revision',
  'product',
  'channel',
  'first_move_in_date',
  'provider_outcome',
  'unit_id',
] as const;

/** One explicit command only. 접수 영수증만 검증하며 성공으로 해석하지 않는다. */
export async function submitEzremsUnitTransition(
  client: ServiceClient,
  unitId: string,
  command: EzremsTransitionCommand,
): Promise<EzremsUnitTransitionReceipt> {
  const bad = (status?: number) =>
    invalid('외부 판매 요청 또는 응답을 확인하지 못했습니다.', status);
  const keys = [
    'command_id',
    'target',
    'expected_sale_revision',
    'first_move_in_date',
    'reason',
    ...(Object.hasOwn(command, 'daily_price_minor') ? ['daily_price_minor'] : []),
  ];
  if (
    !isUuid(unitId) ||
    !record(command) ||
    Object.keys(command).length !== keys.length ||
    !keys.every((key) => Object.hasOwn(command, key)) ||
    !isUuid(command.command_id) ||
    !['MIDTERM', 'GENERAL'].includes(command.target) ||
    !revision(command.expected_sale_revision) ||
    typeof command.reason !== 'string' ||
    !command.reason.trim() ||
    Array.from(command.reason).length > 500 ||
    (command.target === 'GENERAL'
      ? command.first_move_in_date !== null || Object.hasOwn(command, 'daily_price_minor')
      : (Object.hasOwn(command, 'daily_price_minor') && !positive(command.daily_price_minor)) ||
        typeof command.first_move_in_date !== 'string' ||
        !date(command.first_move_in_date))
  )
    throw bad();
  const v = await client.post<unknown>(`admin/units/${unitId}/ezrems-transition`, {
    json: command,
  });
  if (
    !record(v) ||
    !exact(v, receiptKeys) ||
    !sameId(v.command_id, command.command_id) ||
    !sameId(v.unit_id, unitId) ||
    !isUuid(v.room_id) ||
    !revision(v.revision) ||
    typeof v.product !== 'string' ||
    typeof v.channel !== 'string' ||
    !date(v.first_move_in_date) ||
    !nullableString(v.provider_outcome)
  )
    throw bad(202);
  return v as unknown as EzremsUnitTransitionReceipt;
}

const statusKeys = [
  'command_id',
  'room_id',
  'target',
  'outcome',
  'transport',
  'readback_matches',
  'reason',
  'current_sale_revision',
  'pending',
  'unit_id',
] as const;

export async function readEzremsUnitTransitionStatus(
  client: ServiceClient,
  unitId: string,
  commandId: string,
  target: EzremsTransitionCommand['target'],
): Promise<EzremsUnitTransitionStatus> {
  const bad = () => invalid('외부 판매 요청 상태를 확인하지 못했습니다.');
  if (!isUuid(unitId) || !isUuid(commandId) || !['MIDTERM', 'GENERAL'].includes(target))
    throw bad();
  const v = await client.get<unknown>(
    `admin/units/${unitId}/ezrems-transition-commands/${commandId}`,
    { headers: { 'Cache-Control': 'no-store' } },
  );
  if (
    !record(v) ||
    !exact(v, statusKeys) ||
    !sameId(v.command_id, commandId) ||
    !sameId(v.unit_id, unitId) ||
    !isUuid(v.room_id) ||
    v.target !== target ||
    !['PENDING', 'UNKNOWN', 'APPLIED', 'NOT_APPLIED'].includes(String(v.outcome)) ||
    !['UNKNOWN', 'DELIVERED'].includes(String(v.transport)) ||
    !(v.readback_matches === null || typeof v.readback_matches === 'boolean') ||
    !nullableString(v.reason) ||
    !revision(v.current_sale_revision) ||
    typeof v.pending !== 'boolean'
  )
    throw bad();
  return v as unknown as EzremsUnitTransitionStatus;
}
